import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {fetchInstitutionsOnce, resetInstitutionsCache} from "../../api/institutionsCache";

interface InstitutionDto {
    id: number;
    name: string;
    form13FAumTierEnum: AumTier;
    ticker: string | null;
}

interface Props {
    selectedId: number;
    onChange: (id: number) => void;
}

export type AumTier = "SMALL" | "MEDIUM" | "LARGE" | "MEGA" | "TITAN" | "UNCLASSIFIED";

const TIER_RANK: Record<AumTier, number> = { TITAN: 5, MEGA: 4, LARGE: 3, MEDIUM: 2, SMALL: 1, UNCLASSIFIED: 0 };
const TIER_RANGE_HINT: Partial<Record<AumTier, string>> = { SMALL: "$100M–$1B", MEDIUM: "$1B–$10B", LARGE: "$10B–$100B", MEGA: "$100B–$500B", TITAN: "$500B+" };
const TIER_LABEL: Record<AumTier, string> = { SMALL: "Small", MEDIUM: "Medium", LARGE: "Large", MEGA: "Mega", TITAN: "Titan", UNCLASSIFIED: "Unclassified" };

const TIER_COLORS_SOLID: Record<AumTier, { bg: string; fg: string }> = {
    SMALL: { bg: "#10B981", fg: "#062012" },
    MEDIUM:{ bg: "#0EA5E9", fg: "#0b2547" },
    LARGE: { bg: "#6366F1", fg: "#16122e" },
    MEGA:  { bg: "#d84a2d", fg: "#1e1440" },
    TITAN: { bg: "#F59E0B", fg: "#2a1800" },
    UNCLASSIFIED: { bg: "#71717A", fg: "#0b0b0b" },
};
const TIER_COLORS_TINT: Record<AumTier, { bg: string; fg: string }> = {
    SMALL: { bg: "rgba(16,185,129,0.22)", fg: "#EAEAEA" },
    MEDIUM:{ bg: "rgba(14,165,233,0.22)", fg: "#EAEAEA" },
    LARGE: { bg: "rgba(99,102,241,0.22)", fg: "#EAEAEA" },
    MEGA:  { bg: "rgba(139,92,246,0.22)", fg: "#EAEAEA" },
    TITAN: { bg: "rgba(245,158,11,0.22)", fg: "#EAEAEA" },
    UNCLASSIFIED: { bg: "rgba(113,113,122,0.22)", fg: "#EAEAEA" },
};

export const AumPill: React.FC<{ tier: AumTier; variant?: "solid" | "tint" }> = ({ tier, variant = "solid" }) => {
    const { bg, fg } = variant === "tint" ? TIER_COLORS_TINT[tier] : TIER_COLORS_SOLID[tier];
    const title = TIER_RANGE_HINT[tier] ? `${TIER_LABEL[tier]} · ${TIER_RANGE_HINT[tier]} (13F value)` : TIER_LABEL[tier];
    return (
        <span
            title={title}
            aria-label={title}
            style={{
                display: "inline-block",
                padding: "2px 8px",
                fontSize: 12,
                fontWeight: 700,
                borderRadius: 999,
                backgroundColor: bg,
                color: fg,
                lineHeight: "14px",
                whiteSpace: "nowrap",
                boxShadow: "0 0 0 1px rgba(0,0,0,0.35) inset",
            }}
        >
      {TIER_LABEL[tier]}
    </span>
    );
};

export const TickerPill: React.FC<{ ticker: string }> = ({ ticker }) => (
    <span
        title={ticker.toUpperCase()}
        style={{
            display: "inline-block",
            padding: "2px 8px",
            fontSize: 12,
            borderRadius: 999,
            border: "1px solid rgba(255,255,255,0.28)",
            color: "#eaeaea",
            whiteSpace: "nowrap",
            marginRight: 8,
            opacity: 0.95,
            fontWeight: 600,
            letterSpacing: 0.2,
        }}
    >
    {ticker.toUpperCase()}
  </span>
);

/** Only show title tooltip when text is truncated */
const OverflowLabel: React.FC<{
    text: string;
    style?: React.CSSProperties;
}> = ({ text, style }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [showTitle, setShowTitle] = useState(false);

    const check = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        setShowTitle(el.scrollWidth > el.clientWidth);
    }, []);

    useEffect(() => { check(); }, [text, check]);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const RO = (window as any).ResizeObserver as typeof ResizeObserver | undefined;
        if (!RO) {
            // Fallback: re-check on window resize
            const onResize = () => check();
            window.addEventListener("resize", onResize);
            return () => window.removeEventListener("resize", onResize);
        }
        const ro = new RO(() => check());
        ro.observe(el);
        return () => ro.disconnect();
    }, [check]);

    return (
        <div
            ref={ref}
            title={showTitle ? text : undefined}
            style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                ...style,
            }}
        >
            {text}
        </div>
    );
};

// Virtualization constants
const ROW_HEIGHT = 44;
const MAX_DROPDOWN_HEIGHT = 300;
const OVERSCAN = 8;

const InstitutionSelector: React.FC<Props> = ({ selectedId, onChange }) => {
    const [institutions, setInstitutions] = useState<InstitutionDto[]>([]);
    const [search, setSearch] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const didAutoSelectRef = useRef(false);

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [scrollTop, setScrollTop] = useState(0);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const items = await fetchInstitutionsOnce();
            if (!Array.isArray(items) || items.length === 0) {
                setInstitutions([]);
                setError("Could not load institutions (empty response). Is the server available?");
            } else {
                setInstitutions(items as InstitutionDto[]);
            }
        } catch (e: any) {
            setInstitutions([]);
            const msg =
                e?.response?.data?.message ||
                e?.message ||
                "Failed to fetch institutions. Check your connection or server status.";
            setError(String(msg));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { void load(); }, [load]);

    const retry = useCallback(() => {
        resetInstitutionsCache();
        void load();
    }, [load]);

    type Row = {
        id: number;
        name: string;
        nameLower: string;
        ticker: string | null;
        tickerLower: string | null;
        tierUi: AumTier;
    };

    const rows: Row[] = useMemo(
        () =>
            institutions.map((i) => {
                const ticker = i.ticker || null;
                return {
                    id: i.id,
                    name: i.name,
                    nameLower: i.name.toLowerCase(),
                    ticker,
                    tickerLower: ticker ? ticker.toLowerCase() : null,
                    tierUi: i.form13FAumTierEnum as AumTier,
                };
            }),
        [institutions]
    );

    const sorted = useMemo(() => {
        const copy = [...rows];
        copy.sort((a, b) => {
            if (TIER_RANK[b.tierUi] !== TIER_RANK[a.tierUi]) return TIER_RANK[b.tierUi] - TIER_RANK[a.tierUi];
            return a.name.localeCompare(b.name);
        });
        return copy;
    }, [rows]);

    useEffect(() => {
        if (didAutoSelectRef.current) return;
        if (error || loading) return;
        if (!sorted.length) return;
        didAutoSelectRef.current = true;
        onChange(sorted[0].id);
    }, [sorted, onChange, error, loading]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase().trim();
        if (!q) return sorted;
        return sorted.filter((r) => r.nameLower.includes(q) || (!!r.tickerLower && r.tickerLower.includes(q)));
    }, [sorted, search]);

    const selected = useMemo(() => sorted.find((r) => r.id === selectedId) || null, [sorted, selectedId]);

    const selectedFilteredIndex = useMemo(
        () => filtered.findIndex((i) => i.id === selectedId),
        [filtered, selectedId]
    );

    const handleSelect = useCallback((inst: Row) => {
        onChange(inst.id);
        setSearch("");
        setShowDropdown(false);
        setHighlightedIndex(0);
    }, [onChange]);

    useEffect(() => {
        setHighlightedIndex((p) => Math.max(0, Math.min(p, Math.max(0, filtered.length - 1))));
    }, [filtered.length]);

    useEffect(() => {
        if (showDropdown && search.trim() === "" && selectedFilteredIndex >= 0) {
            setHighlightedIndex(selectedFilteredIndex);
        }
    }, [showDropdown, search, selectedFilteredIndex]);

    const ensureIndexVisible = useCallback((idx: number) => {
        const container = scrollRef.current;
        if (!container) return;
        const viewTop = container.scrollTop;
        const viewBottom = viewTop + container.clientHeight;
        const rowTop = idx * ROW_HEIGHT;
        const rowBottom = rowTop + ROW_HEIGHT;

        if (rowTop < viewTop) {
            container.scrollTop = rowTop;
            setScrollTop(container.scrollTop);
        } else if (rowBottom > viewBottom) {
            container.scrollTop = rowBottom - container.clientHeight;
            setScrollTop(container.scrollTop);
        }
    }, []);

    useEffect(() => { ensureIndexVisible(highlightedIndex); }, [highlightedIndex, ensureIndexVisible]);

    const onInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setSearch(val);
        setShowDropdown(true);
        setHighlightedIndex(0);
        requestAnimationFrame(() => ensureIndexVisible(0));
    }, [ensureIndexVisible]);

    const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!showDropdown) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex((p) => {
                const next = Math.min(p + 1, filtered.length - 1);
                requestAnimationFrame(() => ensureIndexVisible(next));
                return next;
            });
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex((p) => {
                const next = Math.max(p - 1, 0);
                requestAnimationFrame(() => ensureIndexVisible(next));
                return next;
            });
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (filtered[highlightedIndex]) handleSelect(filtered[highlightedIndex]);
        } else if (e.key === "Escape") {
            setShowDropdown(false);
        }
    }, [showDropdown, filtered, highlightedIndex, handleSelect, ensureIndexVisible]);

    const containerHeight = Math.min(MAX_DROPDOWN_HEIGHT, filtered.length * ROW_HEIGHT) || MAX_DROPDOWN_HEIGHT;
    const totalHeight = filtered.length * ROW_HEIGHT;
    const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
    const endIndex = Math.min(filtered.length - 1, Math.floor((scrollTop + containerHeight) / ROW_HEIGHT) + OVERSCAN);
    const visibleItems = filtered.slice(startIndex, endIndex + 1);
    const offsetY = startIndex * ROW_HEIGHT;

    const selectedPlaceholder = useMemo(() => {
        if (loading && !selected) return "Loading institutions…";
        if (error && !selected)   return "Institution list unavailable";
        if (!selected)            return "Search institution...";
        return selected.name;
    }, [selected, loading, error]);

    return (
        <div style={{ position: "relative", margin: "0 0 8px 0", backgroundColor: "#1E1E1E", color: "#EAEAEA" }}>
            {error && (
                <div
                    role="alert"
                    style={{
                        marginBottom: 10,
                        padding: "8px 12px",
                        background: "#3b1e1e",
                        border: "1px solid #a44",
                        borderRadius: 8,
                        color: "#ffbcbc",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                    }}
                >
                    <span style={{ flex: 1 }}>{error}</span>
                    <button
                        onClick={retry}
                        style={{
                            padding: "6px 10px",
                            background: "#222",
                            color: "#fff",
                            border: "1px solid #666",
                            borderRadius: 6,
                            cursor: "pointer",
                        }}
                    >
                        Retry
                    </button>
                </div>
            )}

            <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 8 }}>
                <input
                    type="text"
                    value={search}
                    onChange={onInputChange}
                    onFocus={(e) => {
                        if (loading || error || institutions.length === 0) return;
                        setShowDropdown(true);
                        if (search.trim() === "" && selectedFilteredIndex >= 0) {
                            setHighlightedIndex(selectedFilteredIndex);
                            requestAnimationFrame(() => ensureIndexVisible(selectedFilteredIndex));
                        }
                        requestAnimationFrame(() => {
                            try { e.currentTarget.setSelectionRange(0, e.currentTarget.value.length); } catch {}
                        });
                    }}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                    onKeyDown={onKeyDown}
                    placeholder={selectedPlaceholder}
                    // no title on input (tooltip only for truncated dropdown items)
                    disabled={loading || !!error || institutions.length === 0}
                    style={{
                        width: 360,
                        padding: "8px 12px",
                        backgroundColor: loading || error ? "#2a2a2a" : "#181818",
                        color: loading || error ? "#9a9a9a" : "#e0e0e0",
                        fontWeight: 500,
                        letterSpacing: 0.15,
                        border: "1px solid rgba(255,255,255,0.16)",
                        borderRadius: 10,
                        outline: "none",
                        transition: "box-shadow .15s ease, border-color .15s ease",
                        boxShadow: "none",
                        cursor: loading || error ? "not-allowed" : "text",
                    }}
                    aria-label="Institution"
                />

                {selected && !loading && !error && (
                    <div style={{ marginLeft: 6, display: "flex", alignItems: "center" }}>
                        {selected.ticker ? <TickerPill ticker={selected.ticker} /> : null}
                        <AumPill tier={selected.tierUi} />
                    </div>
                )}
            </div>

            {showDropdown && filtered.length > 0 && !loading && !error && (
                <div
                    ref={scrollRef}
                    onScroll={(e) => setScrollTop((e.target as HTMLDivElement).scrollTop)}
                    style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        width: 360,
                        height: containerHeight,
                        backgroundColor: "#2C2C2C",
                        border: "1px solid #444",
                        borderTop: "none",
                        overflowY: "auto",
                        zIndex: 10,
                        borderRadius: 10,
                        marginTop: 6,
                        boxShadow: "0 6px 16px rgba(0,0,0,0.35)",
                        willChange: "transform",
                    }}
                >
                    <div style={{ height: totalHeight, position: "relative" }}>
                        <div style={{ position: "absolute", top: 0, left: 0, right: 0, transform: `translateY(${offsetY}px)` }}>
                            {visibleItems.map((inst, i) => {
                                const index = startIndex + i;
                                const isSelected = inst.id === selectedId;
                                const isHighlighted = index === highlightedIndex;

                                return (
                                    <div
                                        key={inst.id}
                                        onMouseDown={() => handleSelect(inst)}
                                        onMouseEnter={() => setHighlightedIndex(index)}
                                        aria-selected={isSelected}
                                        style={{
                                            height: ROW_HEIGHT,
                                            display: "flex",
                                            alignItems: "center",
                                            padding: "10px 12px",
                                            cursor: "pointer",
                                            backgroundColor: isHighlighted ? "#353535" : "transparent",
                                            color: isSelected ? "#f3f3f3" : "#d8d8d8",
                                            fontWeight: isSelected ? 600 : 400,
                                            borderLeft: isSelected ? "2px solid #f3f3f3" : "2px solid transparent",
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, width: "100%" }}>
                                            <OverflowLabel text={inst.name} />
                                            <div style={{ display: "flex", alignItems: "center" }}>
                                                {inst.ticker ? <TickerPill ticker={inst.ticker} /> : null}
                                                <AumPill tier={inst.tierUi} />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InstitutionSelector;
